import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { ACTIVE_URL } from "@/constants";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface Partner {
  id: number;
  name: string;
  logo_url: string;
  website_url?: string;
}

function PartnerRow({ partners, direction }: { partners: Partner[]; direction: "forward" | "backward" }) {
  const [emblaRef] = useEmblaCarousel(
    { loop: true, dragFree: true, align: "start", watchDrag: false },
    [AutoScroll({ direction, speed: 1, stopOnInteraction: false, stopOnMouseEnter: false, stopOnFocusIn: false })]
  );

  return (
    <div
      className="w-full lg:w-[1128px] lg:mx-auto overflow-hidden [mask-image:linear-gradient(to_right,transparent_0%,black_5%,black_95%,transparent_100%)]"
      ref={emblaRef}
    >
      <div className="flex gap-8 px-8">
        {partners.map((p) => {
          const cardClass = "shrink-0 w-36 h-36 bg-white rounded-xl flex items-center justify-center p-4";
          const logo = <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" />;
          return p.website_url ? (
            <a
              key={p.id}
              href={p.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${cardClass}`}
            >
              {logo}
            </a>
          ) : (
            <div key={p.id} className={cardClass}>
              {logo}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HomePartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const { ref: headerRef, visible: headerVisible } = useScrollReveal(0.1);

  const sectionRef = useRef<HTMLElement>(null);
  const [sectionVisible, setSectionVisible] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) { setSectionVisible(true); observer.disconnect(); } },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${ACTIVE_URL}/api/partners/`)
      .then((r) => r.json())
      .then(setPartners)
      .catch(() => setPartners([]));
  }, []);

  const row1 = partners;
  const row2 = [...partners].reverse();

  return (
    <section ref={sectionRef} className="w-full dot-grid pb-20 pt-16 md:pt-20 overflow-hidden">
      <div
        ref={headerRef}
        className="transition-all duration-700"
        style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
        }}
      >
        <h3 className="text-4xl lg:text-5xl text-white text-center mb-4">
          Οι Συνεργάτες μας
        </h3>
        <p className="text-white/60 text-center text-lg mb-12 px-4">
          Περισσότερα από <span className="text-[#00d9d9] font-semibold">1.000</span> σημεία εξαργύρωσης σε όλη την Ελλάδα
        </p>
      </div>

      {partners.length > 0 && (
        <div
          className="flex flex-col gap-12 mb-12 transition-opacity duration-1000"
          style={{ opacity: sectionVisible ? 1 : 0, transitionDelay: '200ms' }}
        >
          <PartnerRow partners={row1} direction="forward" />
          <PartnerRow partners={row2} direction="backward" />
        </div>
      )}

      <div
        className="flex justify-center transition-opacity duration-1000"
        style={{ opacity: sectionVisible ? 1 : 0, transitionDelay: '350ms' }}
      >
        <Link
          to="/partners"
          className="px-10 py-4 rounded-full bg-[#00d9d9] text-black font-bold font-press-start text-sm hover:scale-105 transition-transform duration-200"
        >
          Δες όλους τους συνεργάτες
        </Link>
      </div>
    </section>
  );
}
